import {useMemo} from 'react';
import classNames from 'classnames';
import {DisabledIcon} from '@shopify/polaris-icons';
import type {CountryCode} from 'types/CountryCode';
import {useFlagImageSource, fallbackImage} from 'components/Flag';
import type {Market} from 'utilities/use-shop-locales-and-markets';

import styles from './MarketFlag.scss';

function hash(input: string) {
  let hash = 0;
  if (input.length === 0) return hash;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

function gradientVariation(marketName: string) {
  if (marketName === "Rohit's market") {
    return 'Sparkles';
  }
  return Math.abs(hash(marketName) % 12) + 1;
}

export interface Props {
  market?: Market;
  countryCode?: CountryCode;
  size?: 'x-small' | 'small' | 'base' | 'large' | 'perfect-24';
}

export function MarketFlag({market, size = 'base', countryCode}: Props) {
  const sizeClassName = size !== 'base' && styles[`MarketFlag--${size}`];
  const {getFlagImageSource, loading} = useFlagImageSource();

  function renderRegionFlag(code: CountryCode) {
    const flagImage = getFlagImageSource(code);
    const defaultClassName =
      flagImage === fallbackImage && styles['MarketFlag--fallback'];
    return (
      <div
        aria-hidden="true"
        className={classNames(
          styles.MarketFlag,
          sizeClassName,
          defaultClassName,
        )}
      >
        <img src={flagImage} alt="" />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={classNames(
          styles.MarketFlag,
          styles.MarketFlag__Placeholder,
          sizeClassName,
        )}
        aria-hidden="true"
      />
    );
  }

  if (countryCode) {
    return renderRegionFlag(countryCode);
  }

  if (!market) {
    return (
      <div
        className={classNames(
          styles.MarketFlag,
          styles.IconFlag,
          sizeClassName,
        )}
        aria-hidden="true"
      >
        <div className={styles.IconFlag__Icon}>
          <DisabledIcon />
        </div>
      </div>
    );
  }

  if (market.regions?.length === 1) {
    return renderRegionFlag(market.regions[0].code as CountryCode);
  }

  const {name} = market;
  return (
    <div
      aria-hidden="true"
      className={classNames(
        styles.MarketFlag,
        styles.MarketFlag__Gradient,
        styles[`MarketFlag__Gradient--${gradientVariation(name)}`],
        sizeClassName,
      )}
    >
      <MarketInitials marketName={name} />
    </div>
  );
}

const notReplacedCharacters =
  /[^A-zÀ-ÖØ-öø-įĴ-őŔ-žǍ-ǰǴ-ǵǸ-țȞ-ȟȤ-ȳɃɆ-ɏḀ-ẞƀ-ƓƗ-ƚƝ-ơƤ-ƥƫ-ưƲ-ƶẠ-ỿ- ]/g;

export function getMarketInitials(marketName: string) {
  // Remove non alphanumeric characters
  const alphaNumericName = marketName
    .replace(/['’]/g, '')
    .replace(notReplacedCharacters, ' ');

  const words = alphaNumericName
    // split and trim each word, using space or hyphen
    .split(/[ -]/g)
    .map((word) => word.trim())
    // remove blank words
    .filter((word) => word.length);

  // if we have no words, the name only contained special characters, return the first two
  if (words.length === 0) {
    return marketName.toLocaleUpperCase().slice(0, 2);
  }

  // a single word name returns the first two letters of the name
  if (words.length === 1) {
    return words[0].toLocaleUpperCase().slice(0, 2);
  }

  // select first letters of a multi word name
  return words.slice(0, 2).reduce((acc, word) => {
    return acc + word.charAt(0).toLocaleUpperCase();
  }, '');
}

function MarketInitials({marketName}: {marketName: string}) {
  const marketInitials = useMemo(
    () => getMarketInitials(marketName),
    [marketName],
  );

  return <span>{marketInitials}</span>;
}
